import type { PatientRecord } from '@viome/viome-base/src/usecase/PatientContext/PatientContext.model';
import { getIsoString } from '@/components/utils/utils';
import useRegisterStore, { PhoneCode } from '../useRegistrationStore';

/**
 * Normalized prefill shape that maps directly onto the registration store
 * setters. Source adapters (patient-record API, user profile) produce this
 * shape so the store-setting logic lives in one place — `applyRegistrationPrefill`.
 */
export interface RegistrationPrefillData {
  email: string;
  firstName: string;
  lastName: string;
  /** Already ISO-normalized by the adapter. */
  dob: string;
  gender: string;
  phone: string;
  phoneCode: PhoneCode;
  country: string;
  street: string;
  apartment: string;
  city: string;
  state: string;
  zip: string;
  smsConsent: boolean;
}

/**
 * The registration gender radio uses capitalized values ("Male" / "Female"),
 * but the patient-record API returns lowercase ("male" / "female"). Normalize
 * so the prefilled value matches a radio option; unknown values pass through.
 */
const normalizeGender = (gender: string | null): string => {
  if (!gender) return '';
  const lower = gender.toLowerCase();
  if (lower === 'male') return 'Male';
  if (lower === 'female') return 'Female';
  return gender;
};

/**
 * Adapter: DE-336 patient-record (API 1) → normalized prefill.
 * The contract's `phone` already includes the country code, so we only set
 * `countryCode` from the address country; `countryPhoneCode` is left blank
 * (the contact form lets the user confirm it).
 */
export const mapPatientRecordToPrefill = (
  patient: PatientRecord,
): RegistrationPrefillData => {
  const address = patient.address;
  return {
    email: patient.email ?? '',
    firstName: patient.firstName ?? '',
    lastName: patient.lastName ?? '',
    dob: patient.dob ? getIsoString(patient.dob) : '',
    gender: normalizeGender(patient.gender),
    phone: patient.phone ?? '',
    phoneCode: { countryPhoneCode: '', countryCode: address?.country ?? '' },
    country: address?.country ?? '',
    street: address?.line1 ?? '',
    apartment: address?.line2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    zip: address?.zip ?? '',
    smsConsent: false,
  };
};

/**
 * Applies a normalized prefill onto the registration store. Shared by the
 * patient-context flow (API 1) and the existing multi-kit flow (user profile).
 */
export const useRegistrationPrefill = () => {
  const {
    setAddressDetails,
    setPersonalInformation,
    setOffers,
    setAccountCreation,
  } = useRegisterStore();

  /**
   * @param options.includeAddress when `false`, the postal-address fields
   *   (country/street/apartment/city/state/zip + phone country code) are left
   *   blank so the user enters them themselves — used for NEW patient users
   *   (DE-336): we don't pre-fill the address even though API 1 returns it.
   *   Name/dob/gender/email/phone are still prefilled. Defaults to `true`.
   */
  const applyRegistrationPrefill = (
    data: RegistrationPrefillData,
    options?: { includeAddress?: boolean },
  ) => {
    const includeAddress = options?.includeAddress ?? true;
    setAddressDetails({
      country: includeAddress ? data.country : '',
      street: includeAddress ? data.street : '',
      apartment: includeAddress ? data.apartment : '',
      city: includeAddress ? data.city : '',
      phoneCode: includeAddress
        ? data.phoneCode
        : { countryPhoneCode: '', countryCode: '' },
      phone: data.phone,
      state: includeAddress ? data.state : '',
      zip: includeAddress ? data.zip : '',
    });
    setPersonalInformation(data.firstName, data.lastName, data.dob, data.gender);
    setOffers(data.smsConsent);
    setAccountCreation(data.email, '');
  };

  return { applyRegistrationPrefill };
};
